// Die Projektseite — die wichtigste Arbeitsflaeche der App.
//
// UMBAU (Redesign-Paket 2). Gemessener Befund davor: die erste Aufgabenzeile
// sass bei y=759 von 900 px; auf einem 13"-Notebook (1024x768) war ohne
// Scrollen KEINE Aufgabe sichtbar. Darueber stapelten sich acht Baender:
// Brotkruemel, hoher Projektkopf, drei Info-Karten, Fortschrittskarte, zwei
// Werkzeugzeilen, Phasen-Kopf, Tabellenkopf.
//
// Was sich geaendert hat:
//  1. Brotkruemel, Projektname und die Projektaktionen fuellen jetzt die EINE
//     App-Kopfzeile (`usePageHeader`) statt eines eigenen Kastens. Der Name
//     bleibt inline editierbar — er reist als `titleNode` mit, `title` bleibt
//     der Klartext und damit der zugaengliche Name der einzigen `<h1>`.
//  2. Der Ansichts-Umschalter ist `ui/ViewTabs` in dieser Kopfzeile. Der alte
//     `components/ViewPicker` (Dropdown mit EMOJI-Symbolen — `icon: string;
//     // emoji per UX spec`) ist geloescht; die Symbole sind lucide-Icons aus
//     `project/viewMeta.ts`.
//  3. Werkzeugzeile 1 ist damit aufgeloest: was von ihr blieb (Typ, Erledigte
//     ausblenden, Ansichten, Sichtung) sitzt rechts NEBEN den Tabs
//     (`project/ProjectViewControls`). Im Inhalt bleibt genau EINE
//     Werkzeugzeile uebrig — die von `views/TableView`.
//  4. Projektkopf, Info-Karten und Fortschrittskarte sind zu einem kompakten
//     Band mit einklappbaren Details geworden (`project/ProjectOverview`),
//     standardmaessig zugeklappt.
//  5. Lade- und Ladefertig-Zustand liegen in derselben `PageShell` — vorher
//     `max-w-7xl` gegen volle Breite, die Seite sprang beim Laden.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Inbox, Info } from "lucide-preact";
import { uxEnabled } from "../lib/ux";
import { BottomSheet } from "../components/BottomSheet";
import { Layout } from "../components/Layout";
import { ShareDialog } from "../components/ShareDialog";
import { useMe } from "../lib/auth";
import { type ViewSpec } from "../components/SavedViews";
import { TaskModal } from "../components/TaskModal";
import { openProjectTaskSig, parseAndStripTaskParam } from "../store/deepLinks";
import { TableView } from "../views/TableView";
import { GanttView } from "../views/GanttView";
import { KanbanView } from "../views/KanbanView";
import { CalendarView } from "../views/CalendarView";
import { MilestonesView } from "../views/MilestonesView";
import { InlineEdit } from "../components/InlineEdit";
import { ProjectSettingsPanel } from "../components/ProjectSettingsPanel";
import { PageShell } from "../components/ui/PageShell";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { ViewTabs } from "../components/ui/ViewTabs";
import { usePageHeader } from "../lib/pageHeader";
import { PROJECT_VIEWS } from "./project/viewMeta";
import { ProjectActions } from "./project/ProjectActions";
import { ProjectOverview } from "./project/ProjectOverview";
import { ProjectViewControls } from "./project/ProjectViewControls";
import { ProjectAiSummary } from "./project/ProjectAiSummary";
import { ProjectError, ProjectNotFound } from "./project/ProjectFallbacks";
import { useProjectDetail, patchProject, refreshProjectDetail } from "../store/project";
import { useStatuses } from "../store/statuses";
import { useUsers } from "../store/users";
import { useFavorites, toggleFavorite, trackRecent } from "../store/favorites";
import { TriageQueue } from "../components/TriageQueue";
import { ProjectAnalytics } from "../components/ProjectAnalytics";
import { listTriage, aiSummarizeProject, ApiError } from "../lib/api";
import { toastError } from "../lib/toast";
import { type GroupBy } from "../lib/grouping";
import { type TableColumnId } from "../lib/columns";
import { startStream, type TaskEventData } from "../lib/sse";
import { type FilterRule } from "../lib/viewState";
import {
  loadProjectViewState,
  saveProjectViewState,
} from "./project/viewStatePersistence";

interface RouteProps {
  params: { id: string };
}

export function Project(routeProps: RouteProps): preact.JSX.Element {
  const id = Number(routeProps.params.id);
  if (!Number.isFinite(id) || id <= 0) {
    return <ProjectNotFound />;
  }

  return (
    <Layout activeProjectId={id}>
      <ProjectInner id={id} />
    </Layout>
  );
}

function ProjectInner({ id }: { id: number }): preact.JSX.Element {
  const detail = useProjectDetail(id);
  const me = useMe();

  // Deep-link from the global search palette: openProjectTaskSig carries the
  // task id to open. Reading .value subscribes this component, so it fires even
  // when we are already on this project (a same-path navigation wouldn't).
  // View-independent — works regardless of the active tab (incl. analytics).
  const pendingProjectTaskId = openProjectTaskSig.value;
  const [deepLinkTaskId, setDeepLinkTaskId] = useState<number | null>(null);
  useEffect(() => {
    if (pendingProjectTaskId != null) {
      setDeepLinkTaskId(pendingProjectTaskId);
      openProjectTaskSig.value = null;
    }
  }, [pendingProjectTaskId]);
  // Deep-link open: ?task=<id> in der URL — geteilter Link ("Link kopieren") oder
  // eine Teams-/Inbox-Benachrichtigung, die einen echten Seitenaufruf auslöst.
  // Parsen + Entfernen steckt in parseAndStripTaskParam (store/deepLinks.ts),
  // geteilt mit Lists.tsx.
  useEffect(() => {
    const taskId = parseAndStripTaskParam();
    if (taskId != null) setDeepLinkTaskId(taskId);
  }, [id]);
  const allProjectTasks = useMemo(
    () => Object.values(detail.tasksByPhase).flat(),
    [detail.tasksByPhase]
  );
  const deepLinkTask =
    deepLinkTaskId != null
      ? allProjectTasks.find((t) => t.id === deepLinkTaskId) ?? null
      : null;

  // -------------------------------------------------------------------------
  // Per-project view state — persisted via viewStateStore under "project:<id>".
  // view + tableFilter are encoded in ViewState.filters (synthetic rules with
  // field "_view"/"_filter") because their string values don't match ViewKind.
  // groupBy and hiddenColumns map directly to ViewState fields.
  // -------------------------------------------------------------------------
  // Compute the initial state from the store exactly once per mount.
  // A ref holds the result so subsequent renders don't re-read localStorage.
  // The useEffect([id]) below reloads state when the project changes.
  const _initRef = useRef<ReturnType<typeof loadProjectViewState> | null>(null);
  if (_initRef.current === null) _initRef.current = loadProjectViewState(id);
  const _init = _initRef.current;

  const [view, setView] = useState<string>(_init.view);
  // Deep-link hint, e.g. /p/:id?settings=git from the admin git overview's
  // "Vorschläge im Projekt prüfen" link — opens the settings drawer and scrolls
  // to the linked-repositories (git proposals) section.
  const initialSettingsSection = (() => {
    try {
      return new URLSearchParams(window.location.search).get("settings");
    } catch {
      // Malformed query string — an optional deep-link hint, not the primary
      // navigation. Opening the settings drawer without a pre-selected
      // section is a fine fallback.
      return null;
    }
  })();
  const [showSettings, setShowSettings] = useState(initialSettingsSection !== null);
  // UX-Vorschlag P7 (Mobil-Web, nur unter 768 px): Projektband und Werkzeuge
  // wandern in ein „Details"-Sheet, die Liste startet direkt unter den Tabs.
  // Der feste +-Knopf kommt global aus App.tsx. Desktop unverändert.
  const p7 = uxEnabled("ux_web_p7");
  const [showDetails, setShowDetails] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showTriage, setShowTriage] = useState(false);
  // Lifted table-view state so SavedViews can read/restore it.
  const [groupBy, setGroupBy] = useState<GroupBy>(_init.groupBy);
  const [hiddenColumns, setHiddenColumns] = useState<Set<TableColumnId>>(_init.hiddenColumns);
  const [tableFilter, setTableFilter] = useState<string>(_init.tableFilter);
  // Real, shared filter rules (#872 type filter, #873 hide-done). Applied by all
  // four project views through the one predicate in lib/viewState.ts.
  const [viewFilters, setViewFilters] = useState<FilterRule[]>(_init.filters);

  /** Persist the current four-field view state to localStorage. */
  const persist = (
    view: string,
    groupBy: GroupBy,
    hiddenColumns: Set<TableColumnId>,
    tableFilter: string,
    filters: FilterRule[] = viewFilters,
  ): void => {
    saveProjectViewState(id, { view, groupBy, hiddenColumns, tableFilter, filters });
  };

  // Wrapped setters — each setter also persists the full state snapshot.
  const handleSetView = (next: string): void => {
    setView(next);
    persist(next, groupBy, hiddenColumns, tableFilter);
  };
  const handleSetGroupBy = (next: GroupBy): void => {
    setGroupBy(next);
    persist(view, next, hiddenColumns, tableFilter);
  };
  const handleSetHiddenColumns = (next: Set<TableColumnId>): void => {
    setHiddenColumns(next);
    persist(view, groupBy, next, tableFilter);
  };
  const handleSetFilters = (next: FilterRule[]): void => {
    setViewFilters(next);
    persist(view, groupBy, hiddenColumns, tableFilter, next);
  };
  const handleApplySavedView = (spec: ViewSpec): void => {
    const nextView = spec.view_type;
    const nextGroupBy = spec.groupBy;
    const nextHiddenColumns = new Set(spec.hiddenColumns);
    const nextFilter = spec.filter ?? tableFilter;
    setView(nextView);
    setGroupBy(nextGroupBy);
    setHiddenColumns(nextHiddenColumns);
    setTableFilter(nextFilter);
    persist(nextView, nextGroupBy, nextHiddenColumns, nextFilter, viewFilters);
  };
  const [triageCount, setTriageCount] = useState(0);
  const [aiSummaryBusy, setAiSummaryBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummary503, setAiSummary503] = useState(false);

  /** Fetch the current pending-triage count; silently falls back to 0 on error. */
  const refreshTriageCount = (): void => {
    listTriage(id)
      .then((rows) => setTriageCount(rows.length))
      .catch(() => setTriageCount(0));
  };

  // Fetch count on mount and whenever the project changes.
  useEffect(refreshTriageCount, [id]);

  useUsers();
  // Load per-project custom statuses into the store so any child view can read
  // statusesSignal without an additional fetch (no board rendering change yet).
  useStatuses(id);
  const favorites = useFavorites();

  // Track the view in the user's recents list on mount (and when the route
  // id changes — same component instance can be reused across /p/:id swaps).
  useEffect(() => {
    void trackRecent("project", id);
  }, [id]);

  // Project-scoped SSE stream — subscribes to task.* events for this project.
  // Refreshes the task list whenever the server pushes a task change.
  // The stream is stopped and re-started whenever the open project changes.
  useEffect(() => {
    const stop = startStream({
      projects: [id],
      onEvent: (type, data) => {
        if (
          type === "task.created" ||
          type === "task.updated" ||
          type === "task.status"
        ) {
          const taskData = data as TaskEventData;
          if (taskData.projectId === id) {
            void refreshProjectDetail();
          }
        }
      },
    });
    return stop;
  }, [id]);

  // Re-load per-project view state when navigating between projects.
  // Uses the scoped viewStateStore entry so each project remembers its own
  // view / groupBy / hiddenColumns / tableFilter independently.
  useEffect(() => {
    const s = loadProjectViewState(id);
    setView(s.view);
    setGroupBy(s.groupBy);
    setHiddenColumns(s.hiddenColumns);
    setTableFilter(s.tableFilter);
  }, [id]);

  const onExport = (): void => {
    // Browser handles the download — same-origin so the session cookie is sent.
    window.location.href = `/api/projects/${id}/export.xlsx`;
  };

  const handleSummarizeProject = async (): Promise<void> => {
    setAiSummaryBusy(true);
    setAiSummary(null);
    setAiSummary503(false);
    try {
      const result = await aiSummarizeProject(id);
      setAiSummary(result.summary);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setAiSummary503(true);
      } else {
        toastError("Zusammenfassung konnte nicht erstellt werden.");
      }
    } finally {
      setAiSummaryBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Die EINE App-Kopfzeile fuellen.
  //
  // Steht VOR den Abbruch-Rueckgaben unten — `usePageHeader` ist ein Hook und
  // darf nicht hinter einem `return` liegen. Solange das Projekt noch laedt,
  // traegt der Kopf den Brotkruemel und einen Platzhalter-Titel; Tabs und
  // Aktionen brauchen das Projekt und bleiben bis dahin leer.
  // -------------------------------------------------------------------------
  const project = detail.project;
  // Schritt 1 Sharing: Viewer sehen alles, dürfen aber nichts ändern.
  const readOnly = project?.my_role === "viewer";
  const canShare = project?.my_role === "owner" || !!me.user?.is_admin;
  const title = project?.name ?? "Projekt";

  // Ansichts-Bedienelemente als Wert: P7 zeigt sie mobil im Details-Sheet.
  const viewControls = (
    <ProjectViewControls
      projectId={id}
      view={view}
      groupBy={groupBy}
      hiddenColumns={hiddenColumns}
      tableFilter={tableFilter}
      viewFilters={viewFilters}
      onFiltersChange={handleSetFilters}
      onApplySavedView={handleApplySavedView}
      triageOpen={showTriage}
      triageCount={triageCount}
      onToggleTriage={() => {
        const next = !showTriage;
        setShowTriage(next);
        // Refresh count when opening (catches external changes).
        if (next) refreshTriageCount();
      }}
    />
  );

  usePageHeader({
    title,
    // Der Projektname bleibt inline editierbar — `titleNode` ist genau dafuer
    // gebaut. Die Bearbeitungslogik ist UNVERAENDERT (`InlineEdit` + dasselbe
    // `patchProject`), nur ihr Ort ist neu. `title` bleibt der Klartext und
    // damit der zugaengliche Name der einzigen `<h1>` der Seite.
    titleNode: project ? (
      <InlineEdit
        value={project.name}
        onSave={(name) => patchProject(project.id, { name })}
        class="min-w-0 max-w-full"
        ariaLabel="Projekt-Namen bearbeiten"
        readOnly={readOnly}
      />
    ) : undefined,
    breadcrumb: [{ label: "Projekte", href: "/projects" }, { label: title }],
    // Tabs UND die Ansichts-Bedienelemente teilen eine Zeile: links die
    // Ansichts-Tabs mit Unterstrich (Referenz), rechts Typ / Erledigte
    // ausblenden / Ansichten / Sichtung. So bleibt es EIN Band statt zweier.
    tabs: project ? (
      <div class="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <ViewTabs
          items={PROJECT_VIEWS}
          active={view}
          onChange={handleSetView}
          label="Projekt-Ansichten"
        />
        {p7 && (
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            aria-haspopup="dialog"
            class="md:hidden inline-flex min-h-[36px] items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            data-testid="mobile-details-toggle"
          >
            <Info size={14} aria-hidden="true" />
            Details
          </button>
        )}
        {p7 ? <div class="hidden md:block">{viewControls}</div> : viewControls}
      </div>
    ) : undefined,
    actions: project ? (
      <ProjectActions
        project={project}
        readOnly={readOnly}
        canShare={canShare}
        aiEnabled={me.user?.ai_enabled === true}
        aiSummaryBusy={aiSummaryBusy}
        settingsOpen={showSettings}
        onSummarize={() => void handleSummarizeProject()}
        onExport={onExport}
        onShare={() => setShowShare(true)}
        onToggleSettings={() => setShowSettings((v) => !v)}
      />
    ) : undefined,
  });

  if (detail.loading && !project) {
    // Dieselbe `PageShell` wie der geladene Zustand — vorher `max-w-7xl` hier
    // gegen volle Breite dort, wodurch die Seite beim Laden in der Breite sprang.
    return (
      <PageShell width="default">
        <LoadingSkeleton width="33%" height={32} class="mb-3" />
        <LoadingSkeleton width="66%" height={16} class="mb-6" />
        <LoadingSkeleton height={256} />
      </PageShell>
    );
  }

  if (detail.error || !project) {
    return <ProjectError message={detail.error ?? "Projekt nicht gefunden"} />;
  }

  // Kompaktes Band (Status, Zustand, Ziel, Fortschritt, Zustaendige) plus die
  // einklappbaren Details — Begruendung im Kopf von project/ProjectOverview.tsx.
  // Als Wert, weil P7 es mobil zusaetzlich im Details-Sheet zeigt.
  const overview = (
    <ProjectOverview
      project={project}
      readOnly={readOnly}
      canShare={canShare}
      isFavorite={favorites.project.has(project.id)}
      onToggleFavorite={() =>
        void toggleFavorite("project", project.id).catch(() => {
          /* toggleFavorite() hat schon gemeldet */
        })
      }
      onEdit={() => setShowSettings(true)}
    />
  );

  return (
    <>
      {/* Seite und Einstellungs-Panel nebeneinander: Das Panel ist ab `lg`
          `sticky` statt `fixed`, braucht also eine Flex-Zeile als Eltern —
          ohne sie (Redesign #120) rutschte es unter den ganzen Inhalt. */}
      <div class="flex">
        <div class="flex-1 min-w-0">
          <PageShell width="default">
            {/* Kompaktes Band (Status, Zustand, Ziel, Fortschritt, Zustaendige)
                plus die einklappbaren Details — Begruendung der Entscheidung im
                Kopf von project/ProjectOverview.tsx. */}
            {p7 ? <div class="hidden md:block">{overview}</div> : overview}
            {p7 && showDetails && (
              <BottomSheet title="Projekt-Details" onClose={() => setShowDetails(false)}>
                <div class="flex flex-col gap-3">
                  {overview}
                  {viewControls}
                </div>
              </BottomSheet>
            )}
            {/* P7 +-Knopf: global in App.tsx (components/MobileCaptureFab). */}

            <ProjectAiSummary unavailable={aiSummary503} summary={aiSummary} />

            {/* Die gewaehlte Ansicht. Ihre Werkzeugzeile bringt sie selbst mit —
                deshalb steht hier keine mehr. */}
            {view === "table" && (
              <TableView
                project={project}
                tasksByPhase={detail.tasksByPhase}
                readOnly={readOnly}
                controlledGroupBy={groupBy}
                onGroupByChange={handleSetGroupBy}
                controlledHiddenColumns={hiddenColumns}
                onHiddenColumnsChange={handleSetHiddenColumns}
                filters={viewFilters}
              />
            )}
            {view === "timeline" && (
              <GanttView project={project} readOnly={readOnly} filters={viewFilters} />
            )}
            {view === "kanban" && (
              <KanbanView
                project={project}
                tasksByPhase={detail.tasksByPhase}
                readOnly={readOnly}
                filters={viewFilters}
              />
            )}
            {view === "calendar" && (
              <CalendarView
                project={project}
                tasksByPhase={detail.tasksByPhase}
                readOnly={readOnly}
                filters={viewFilters}
              />
            )}
            {view === "milestones" && (
              <MilestonesView project={project} tasksByPhase={detail.tasksByPhase} readOnly={readOnly} />
            )}
            {view === "analytics" && <ProjectAnalytics projectId={project.id} />}

            {/* Sichtung — eingeschaltet ueber den Knopf auf der Tab-Zeile. */}
            {showTriage && (
              <section
                class="mt-6 rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4"
                aria-label="Sichtungsliste"
              >
                <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-on-surface)]">
                  <Inbox size={16} aria-hidden="true" />
                  Eingereichte Aufgaben zur Sichtung
                </h2>
                <TriageQueue
                  projectId={project.id}
                  canEdit={!readOnly}
                  onDecision={refreshTriageCount}
                />
              </section>
            )}
          </PageShell>
        </div>

        {showSettings && !readOnly && (
          <ProjectSettingsPanel
            project={project}
            scrollToSection={initialSettingsSection ?? undefined}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>

      {showShare && (
        <ShareDialog project={project} onClose={() => setShowShare(false)} />
      )}

      {/* Global-search deep-link: open the task detail over any view. */}
      {deepLinkTask && (
        <TaskModal
          task={deepLinkTask}
          siblings={allProjectTasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setDeepLinkTaskId(null)}
          onDeleted={() => setDeepLinkTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </>
  );
}
